import React from "react";

import {
  createFileRoute,
  redirect,
  useLocation,
  useNavigate,
} from "@tanstack/react-router";

import { AppShell } from "@/components/layouts/AppShell";
import { useAuth } from "@/context/auth";
import {
  billingStatusQueryOptions,
  useBillingStatusQuery,
} from "@/queries/hooks/billingHooks";
import type { BillingStatus } from "@/schemas/billing";
import {
  SUBSCRIPTION_BILLING_ENABLED,
  isBillingResolveDismissed,
  shouldRedirectToBillingResolve,
} from "@/utils/billing";

type AppSearch = {
  /** When set, opens the shift-detail modal (ShiftDetailModal) for this shift id. */
  shift?: string;
};

export const Route = createFileRoute("/app/$role/_app")({
  // Declared on the layout so every _app child route inherits `?shift=` — this is
  // what the route-aware shift-detail modal reads/writes (see use-shift-modal).
  validateSearch: (search: Record<string, unknown>): AppSearch => ({
    shift: typeof search.shift === "string" ? search.shift : undefined,
  }),
  // Subscription billing gate (employers only, flag on, account opted in —
  // shouldRedirectToBillingResolve requires status.enabled): send owners with
  // a payment problem to the resolution page before any app route renders.
  beforeLoad: async ({ context, location }) => {
    if (!SUBSCRIPTION_BILLING_ENABLED) return;
    const user = context.auth?.user;
    if (user?.role !== "employer") return;
    let status: BillingStatus;
    try {
      status = await context.queryClient.ensureQueryData({
        ...billingStatusQueryOptions(user.userId),
        retry: false,
      });
    } catch {
      // Billing status unavailable — never lock the owner out over it.
      return;
    }
    if (
      shouldRedirectToBillingResolve({
        flagEnabled: SUBSCRIPTION_BILLING_ENABLED,
        role: user.role,
        status,
        pathname: location.pathname,
        dismissedThisSession: isBillingResolveDismissed(user.userId),
      })
    ) {
      throw redirect({
        to: "/app/$role/billing/resolve",
        params: { role: user.role },
      });
    }
  },
  component: AppLayout,
});

function AppLayout() {
  return (
    <>
      {SUBSCRIPTION_BILLING_ENABLED ? <BillingGateWatcher /> : null}
      <AppShell showOutlet />
    </>
  );
}

/**
 * Keeps the gate live after the initial navigation: billing status refetches
 * on window focus / staleness, and a newly past-due or blocked account is
 * redirected without waiting for the next route change.
 */
function BillingGateWatcher() {
  const { user } = useAuth();
  const { data: status } = useBillingStatusQuery();
  const pathname = useLocation({ select: (l) => l.pathname });
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!user) return;
    if (
      shouldRedirectToBillingResolve({
        flagEnabled: SUBSCRIPTION_BILLING_ENABLED,
        role: user.role,
        status,
        pathname,
        dismissedThisSession: isBillingResolveDismissed(user.userId),
      })
    ) {
      void navigate({
        to: "/app/$role/billing/resolve",
        params: { role: user.role },
        replace: true,
      });
    }
  }, [user, status, pathname, navigate]);

  return null;
}
